import { cva } from "class-variance-authority";
import { cn } from "../../lib/utils.js";
import { TONES, type StatusTone } from "../../lib/tones.js";
import { CATEGORICAL_TINTS, type CategoricalTint } from "../../lib/tints.js";

/* StatusPill's class matrix, split out of StatusPill.tsx so the component
   stays under the 200-line cap (C7). The API notes live on the props in
   StatusPill.tsx. */

/* Soft THEN text, from the one tone map (the class order every toned pill
   has always had). Neutral takes the map's soft paint alone, so its text is
   pb-text: the adoption decision (SN-03, PR 3) moved the neutral pill off
   its muted text. */
const pillTone = (tone: StatusTone) =>
  tone === "neutral"
    ? TONES.neutral.soft
    : cn(TONES[tone].soft, TONES[tone].text);

/** `md` is today's look. `sm` is the dense step for grid cells and map
 *  symbols. `lg` is the 32px step for a workspace control row, with the 8px
 *  dot. `inline` takes no font-size of its own — padding, gap and the marker
 *  are all em-sized, so the pill scales with whatever font-size its parent
 *  sets (a VSM map chip at a given zoom, for example). */
export type StatusPillSize = "sm" | "md" | "lg" | "inline";

/** `soft` (default): today's look. `solid`: saturated fill, its foreground and
 *  a `currentColor` dot; neutral never saturates, so a neutral solid is the
 *  soft pill. `outline`: tone border and tone text on a transparent fill.
 *  `elevated`: tone border and tone text on the card fill with a medium
 *  shadow, for a pill that floats over a tinted canvas lane (usually with
 *  `showDot={false}`). A tint takes soft or outline only. */
export type StatusPillVariant = "soft" | "solid" | "outline" | "elevated";

export const statusPillVariants = cva(
  "inline-flex items-center rounded-full border font-medium",
  {
    variants: {
      tone: {
        healthy: pillTone("healthy"),
        watch: pillTone("watch"),
        critical: pillTone("critical"),
        neutral: pillTone("neutral"),
        info: pillTone("info"),
        ai: pillTone("ai"),
      } satisfies Record<StatusTone, string>,
      size: {
        md: "gap-1.5 px-2 py-0.5 text-dense-1",
        sm: "gap-1 px-1.5 py-px text-dense-3",
        lg: "h-8 gap-2 px-3 text-sm",
        /* `inline` sets no font-size and scales with the parent's, so its
           padding and gap must be em-relative too; the 4px token scale is
           px-based and cannot express that (statuspill-marker-size). */
        // eslint-disable-next-line no-restricted-syntax -- see comment above
        inline: "gap-[0.35em] px-[0.5em] py-[0.05em]",
      } satisfies Record<StatusPillSize, string>,
    },
    defaultVariants: { tone: "healthy", size: "md" },
  },
);

/* Colours for every look but the soft tone pill (default, and neutral solid),
   which the cva `tone` axis paints so today's classes stay byte-identical. A
   tint keeps pb-text; a solid fills its 1px border, so the box holds (Badge). */
export function pillColour(
  variant: StatusPillVariant,
  tone: StatusTone,
  tint: CategoricalTint | undefined,
): string | null {
  if (tint !== undefined) {
    const recipe = CATEGORICAL_TINTS[tint];
    const fill = variant === "outline" ? "bg-transparent" : recipe.fill;
    return cn(fill, recipe.line, "text-pb-text");
  }
  if (variant === "outline")
    return cn("bg-transparent", TONES[tone].border, TONES[tone].text);
  if (variant === "elevated")
    return cn("bg-pb-card", TONES[tone].border, TONES[tone].text, "shadow-md");
  if (variant === "solid" && tone !== "neutral")
    return cn("border-transparent", TONES[tone].solid);
  return null;
}
